import React, { useEffect, useState } from "react";
import { Form, Image, Upload, Select } from "antd";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { UploadDocumentApi } from "../../../../api/request/kyc.js";
import { setUserDetails } from "../../../../store/Slices/userSlice.js";
import LoadableButton from "../../../../components/buttons/LoadableButton.jsx";
import UploadDoc from "../../../../assets/UploadDoc.jsx";
import Lock from "../../../../assets/Lock.jsx";
import Delete from "../../../../assets/Delete.jsx";
import Dragger from "antd/es/upload/Dragger.js";
import { handleApiError } from "../../../../utils/handleApiError.js";
import { useResponsive } from "../../../../hooks/useResponsive.js";

const KycForm = () => {
  const dispatch = useDispatch();
  const { isMobile } = useResponsive()
  const { userDetails } = useSelector((state) => state.user);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [fileFrontList, setFileFrontList] = useState([]);
  const [fileBackList, setFileBackList] = useState([]);
  const [addressProofFrontFileList, setAddressProofFrontFileList] = useState([]);
  const [addressProofBackFileList, setAddressProofBackFileList] = useState([]);
  const [showBothSidesMessage, setShowBothSidesMessage] = useState(false);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
  };

  const handleFrontChange = ({ fileList }) => {
    setFileFrontList(fileList);
    form.setFieldsValue({ UploadDocumentFront: fileList });
    form.setFields([{ name: "UploadDocumentFront", errors: null }]);
    if (fileList.length >= 1 && fileBackList.length >= 1) setShowBothSidesMessage(false);
  };

  const handleBackChange = ({ fileList }) => {
    setFileBackList(fileList);
    form.setFieldsValue({ UploadDocumentBack: fileList });
    form.setFields([{ name: "UploadDocumentBack", errors: null }]);
    if (fileList.length >= 1 && fileFrontList.length >= 1) setShowBothSidesMessage(false);
  };

  const handleAddressProofFrontChange = ({ fileList }) => {
    setAddressProofFrontFileList(fileList);
    form.setFieldsValue({ AddressProofFront: fileList });
    form.setFields([{ name: "AddressProofFront", errors: null }]);
  };

  const handleAddressProofBackChange = ({ fileList }) => {
    setAddressProofBackFileList(fileList);
    form.setFieldsValue({ AddressProofBack: fileList });
    form.setFields([{ name: "AddressProofBack", errors: null }]);
  };

  const FronSideUploadButton = (
    <div className="flex justify-center flex-col items-center  md:py-4 md:px-5">
      <div className="flex justify-center w-auto mb-5 px-4 py-4 bg-gray-100 rounded-md pointer-events-none opacity-75">
        <UploadDoc color="#7367F0"></UploadDoc>
      </div>
      <div className="text-base font-medium capitalize mb-1 pointer-events-none text-gray-500">
        Upload Front side of document
      </div>
      <div className="text-gray-400">
        Drag and drop your files here or{" "}
        <span className="text-[#7367F0] cursor-pointer">Click to Select</span>
      </div>
      <div className="text-gray-400">Supported Formets: .jpg,.jpeg,.png,.pdf</div>
    </div>
  );

  const BackSideUploadButton = (
    <div className="flex justify-center flex-col items-center md:py-4 md:px-5">
      <div className="flex justify-center w-auto mb-5 px-4 py-4 bg-gray-100 rounded-md pointer-events-none opacity-75">
        <UploadDoc color="#28C76F"></UploadDoc>
      </div>
      <div className="text-base font-medium capitalize mb-1 pointer-events-none text-gray-500">
        Upload back side of document
      </div>
      <div className="text-gray-400">
        Drag and drop your files here or{" "}
        <span className="text-[#28C76F] cursor-pointer">Click to Select</span>
      </div>
      <div className="text-gray-400">Supported Formets: .jpg,.jpeg,.png,.pdf</div>
    </div>
  );

  const AddressProofFrontUploadButton = (
    <div className="flex justify-center flex-col items-center md:py-4 md:px-5">
      <div className="flex justify-center w-auto mb-5 px-4 py-4 bg-gray-100 rounded-md pointer-events-none opacity-75">
        <UploadDoc color="#7367F0"></UploadDoc>
      </div>
      <div className="text-base font-medium capitalize mb-1 pointer-events-none text-gray-500">
        Upload front side of address proof
      </div>
      <div className="text-gray-400">
        Drag and drop your files here or{" "}
        <span className="text-[#7367F0] cursor-pointer">Click to Select</span>
      </div>
      <div className="text-gray-400">Supported Formets: .jpg,.jpeg,.png,.pdf</div>
    </div>
  );

  const AddressProofBackUploadButton = (
    <div className="flex justify-center flex-col items-center md:py-4 md:px-5">
      <div className="flex justify-center w-auto mb-5 px-4 py-4 bg-gray-100 rounded-md pointer-events-none opacity-75">
        <UploadDoc color="#28C76F"></UploadDoc>
      </div>
      <div className="text-base font-medium capitalize mb-1 pointer-events-none text-gray-500">
        Upload back side of address proof
      </div>
      <div className="text-gray-400">
        Drag and drop your files here or{" "}
        <span className="text-[#28C76F] cursor-pointer">Click to Select</span>
      </div>
      <div className="text-gray-400">Supported Formets: .jpg,.jpeg,.png,.pdf</div>
    </div>
  );

  const handleSubmit = async () => {
    const uploadedFront = fileFrontList.map((file) => file.originFileObj);
    const uploadedBack = fileBackList.map((file) => file.originFileObj);
    const uploadedAddressProofFront = addressProofFrontFileList.map((file) => file.originFileObj);
    const uploadedAddressProofBack = addressProofBackFileList.map((file) => file.originFileObj);
    const frontBackValid = uploadedFront.length === 1 && uploadedBack.length === 1;
    const addressProofValid = uploadedAddressProofFront.length === 1;

    if (!frontBackValid) {
      setShowBothSidesMessage(true);
      toast.error("Please upload both sides of the selected document.");
    }
    if (!addressProofValid) {
      toast.error("Please upload at least front side of the address proof document.");
    }
    if (!frontBackValid || !addressProofValid) {
      return;
    }

    const formData = new FormData();
    formData.append("NationalID", true);
    formData.append("AddressDocument", true);
    formData.append("UserId", userDetails?._id || userDetails?.id);
    formData.append("UploadDocument", uploadedFront[0]);
    formData.append("UploadDocument", uploadedBack[0]);
    formData.append("AddressDocument", uploadedAddressProofFront[0]);
    formData.append("AddressDocument", uploadedAddressProofBack[0]);
    formData.append("UploadSignature", uploadedBack[0]);

    try {
      setIsLoading(true);
      let apiHeaders = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };
      const { data } = await UploadDocumentApi(formData, apiHeaders);
      if (data?.success) {
        dispatch(setUserDetails({ ...userDetails, kycStatus: "Pending" }));
        toast.success(data?.message);
        form.resetFields();
        setFileFrontList([]);
        setFileBackList([]);
        setAddressProofFrontFileList([]);
        setAddressProofBackFileList([]);
        setShowBothSidesMessage(false);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="kyc-container">
      <div className="container">
        <div className="w-full min-h-[calc(100vh-200px)] flex flex-col items-center justify-center">
          <div className="mb-9">
            <div className="flex items-center justify-center mb-3">
              <div className="h-[40px] w-[40px] bg-[#FFF0E1] flex items-center justify-center me-3 rounded-xl">
                <Lock color="#FF9F43"></Lock>
              </div>
              <p className="font-bold text-[#2F2B3D] sm:text-3xl text-xl text-center">
                Upload KYC Documents
              </p>
            </div> 
          </div>
          <Form form={form} onFinish={handleSubmit}>

            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-3`}>
              
              <div className="border border-gray-300 rounded-md p-5 ">
                <p className=" text-[#2F2B3D] text-2xl text-center mb-3">
                  Upload ID Proof
                </p>
                <p className="text-gray-500 text-base text-center mb-3">
                  Please upload clear front and back images of your valid ID for verification.
                </p>
                <div className="flex md:flex-nowrap flex-wrap lg:gap-6 gap-3 justify-center">
                  <div>
                    <Form.Item name="UploadDocumentFront" rules={[{ required: true, message: "Please upload the front side of the document." }]}> 
                      <div className="mb-3">
                      <Dragger
                        accept=".jpg,.jpeg,.png,.pdf"
                        showUploadList={false}
                        beforeUpload={() => false}
                        fileList={fileFrontList}
                        onPreview={handlePreview}
                        onChange={handleFrontChange}
                        className="front-upload"
                        disabled={fileFrontList.length > 0}
                      >
                        <div
                          className="w-full h-full"
                          onDragOver={(e) =>
                            fileFrontList.length > 0 && e.preventDefault()
                          }
                          onDrop={(e) =>
                            fileFrontList.length > 0 && e.preventDefault()
                          }
                        >
                          {fileFrontList.length < 1 ? (
                            FronSideUploadButton
                          ) : (
                            <div className="relative flex justify-center items-center">
                              {fileFrontList[0]?.originFileObj?.type === 'application/pdf' ? (
                                <div className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] bg-gray-100 flex items-center justify-center text-gray-700">
                                  {fileFrontList[0]?.originFileObj?.name || 'PDF'}
                                </div>
                              ) : (
                                <img
                                  src={URL.createObjectURL(
                                    fileFrontList[0].originFileObj
                                  )}
                                  alt="front"
                                  className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] object-contain"
                                />
                              )}
                              <button
                                type="button"
                                className="absolute top-0 -right-0 rounded-md bg-[#FFE2E3] border-primary p-1.5 flex items-center justify-center text-sm font-bolder"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFileFrontList([]);
                                  form.setFieldsValue({ UploadDocumentFront: [] });
                                }}
                              >
                                <Delete />
                              </button>
                            </div>
                          )}
                        </div>
                      </Dragger>
                      </div>
                    </Form.Item>
                  </div>

                  <div className="flex flex-wrap lg:gap-6 gap-3 justify-center">
                    <Form.Item name="UploadDocumentBack" rules={[{ required: true, message: "Please upload the back side of the document." }]}>
                      <div className="mb-3">
                      <Dragger
                        accept=".jpg,.jpeg,.png,.pdf"
                        showUploadList={false}
                        beforeUpload={() => false}
                        fileList={fileBackList}
                        onPreview={handlePreview}
                        onChange={handleBackChange}
                        className="back-upload"
                        disabled={fileBackList.length > 0}
                      >
                        <div
                          className="w-full h-full"
                          onDragOver={(e) =>
                            fileBackList.length > 0 && e.preventDefault()
                          }
                          onDrop={(e) =>
                            fileBackList.length > 0 && e.preventDefault()
                          }
                        >
                          {fileBackList.length < 1 ? (
                            BackSideUploadButton
                          ) : (
                            <div className="relative flex justify-center items-center">
                              {fileBackList[0]?.originFileObj?.type === 'application/pdf' ? (
                                <div className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] bg-gray-100 flex items-center justify-center text-gray-700">
                                  {fileBackList[0]?.originFileObj?.name || 'PDF'}
                                </div>
                              ) : (
                                <img
                                  src={URL.createObjectURL(
                                    fileBackList[0].originFileObj
                                  )}
                                  alt="back"
                                  className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] object-contain"
                                />
                              )}
                              <button
                                type="button"
                                className="absolute top-0 -right-0 rounded-md bg-[#FFE2E3] border-primary p-1.5 flex items-center justify-center text-sm font-bolder"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFileBackList([]);
                                  form.setFieldsValue({ UploadDocumentBack: [] });
                                }}
                              >
                                <Delete />
                              </button>
                            </div>
                          )}
                        </div>
                      </Dragger>
                      </div>
                    </Form.Item>
                  </div>
                </div>

                {showBothSidesMessage && (
                  <div className="text-red-500 mb-4 mt-6 text-center">
                    Please upload both sides of the selected document.
                  </div>
                )}
              </div>

              <div className="border border-gray-300 rounded-md p-5">
                <p className=" text-[#2F2B3D] text-2xl text-center mb-3">
                  Upload Address Proof
                </p>
                <p className="text-gray-500 text-base text-center mb-3">
                  Please upload a valid address proof document.
                </p>
                <div className="flex md:flex-nowrap flex-wrap lg:gap-6 gap-3 justify-center">
                  <div>
                    <Form.Item name="AddressProofFront" rules={[{ required: true, message: "Please upload the front side of the address proof." }]}>
                      <div className="mb-3">
                      <Dragger
                        accept=".jpg,.jpeg,.png,.pdf"
                        showUploadList={false}
                        beforeUpload={() => false}
                        fileList={addressProofFrontFileList}
                        onPreview={handlePreview}
                        onChange={handleAddressProofFrontChange}
                        className="address-proof-front-upload"
                        disabled={addressProofFrontFileList.length > 0}
                      >
                        <div
                          className="w-full h-full"
                          onDragOver={(e) =>
                            addressProofFrontFileList.length > 0 && e.preventDefault()
                          }
                          onDrop={(e) =>
                            addressProofFrontFileList.length > 0 && e.preventDefault()
                          }
                        >
                          {addressProofFrontFileList.length < 1 ? (
                            AddressProofFrontUploadButton
                          ) : (
                            <div className="relative flex justify-center items-center">
                              {addressProofFrontFileList[0]?.originFileObj?.type === 'application/pdf' ? (
                                <div className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] bg-gray-100 flex items-center justify-center text-gray-700">
                                  {addressProofFrontFileList[0]?.originFileObj?.name || 'PDF'}
                                </div>
                              ) : (
                                <img
                                  src={URL.createObjectURL(
                                    addressProofFrontFileList[0].originFileObj
                                  )}
                                  alt="address-proof-front"
                                  className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] object-contain"
                                />
                              )}
                              <button
                                type="button"
                                className="absolute top-0 -right-0 rounded-md bg-[#FFE2E3] border-primary p-1.5 flex items-center justify-center text-sm font-bolder"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAddressProofFrontFileList([]);
                                  form.setFieldsValue({ AddressProofFront: [] });
                                }}
                              >
                                <Delete />
                              </button>
                            </div>
                          )}
                        </div>
                      </Dragger>
                      </div>
                    </Form.Item>
                  </div>
                  <div className="flex flex-wrap lg:gap-6 gap-3 justify-center">
                    <Form.Item name="AddressProofBack" > 
                      <div className="mb-3">
                      <Dragger
                        accept=".jpg,.jpeg,.png,.pdf"
                        showUploadList={false}
                        beforeUpload={() => false}
                        fileList={addressProofBackFileList}
                        onPreview={handlePreview}
                        onChange={handleAddressProofBackChange}
                        className="address-proof-back-upload"
                        disabled={addressProofBackFileList.length > 0}
                      >
                        <div
                          className="w-full h-full"
                          onDragOver={(e) =>
                            addressProofBackFileList.length > 0 && e.preventDefault()
                          }
                          onDrop={(e) =>
                            addressProofBackFileList.length > 0 && e.preventDefault()
                          }
                        >
                          {addressProofBackFileList.length < 1 ? (
                            AddressProofBackUploadButton
                          ) : (
                            <div className="relative flex justify-center items-center">
                              {addressProofBackFileList[0]?.originFileObj?.type === 'application/pdf' ? (
                                <div className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] bg-gray-100 flex items-center justify-center text-gray-700">
                                  {addressProofBackFileList[0]?.originFileObj?.name || 'PDF'}
                                </div>
                              ) : (
                                <img
                                  src={URL.createObjectURL(
                                    addressProofBackFileList[0].originFileObj
                                  )}
                                  alt="address-proof-back"
                                  className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] object-contain"
                                />
                              )}
                              <button
                                type="button"
                                className="absolute top-0 -right-0 rounded-md bg-[#FFE2E3] border-primary p-1.5 flex items-center justify-center text-sm font-bolder"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAddressProofBackFileList([]);
                                  form.setFieldsValue({ AddressProofBack: [] });
                                }}
                              >
                                <Delete />
                              </button>
                            </div>
                          )}
                        </div>
                      </Dragger>
                      </div>
                    </Form.Item>
                  </div>
                </div>

              </div>

            </div>

            <div className="flex justify-end mt-10">
              <LoadableButton
                className={`bg-primary text-white text-base font-semibold rounded-lg px-12 py-2 themeHover duration-500 ${isMobile ? 'w-full' : ''}`}
                disabled={isLoading}
                type="Submit"
                lable="Send Request"
                isLoading={isLoading}
                loadingLable="Sending Request..."
              />
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default KycForm;
